import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  useToast,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
  Text,
  Divider,
  Card,
  CardBody,
  useColorModeValue,
  Flex,
  Textarea,
  Collapse,
  Icon,
} from '@chakra-ui/react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import AdminLayout from '../../layouts/AdminLayout';
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  DragHandleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  ChevronRightIcon,
  ViewIcon,
  ViewOffIcon,
} from '@chakra-ui/icons';
import {
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaTwitter,
  FaTiktok,
  FaLinkedin,
  FaDiscord,
  FaTwitch,
  FaHome,
  FaInfoCircle,
  FaCalendarAlt,
  FaFutbol,
  FaUsers,
  FaTable,
  FaNewspaper,
  FaVideo,
  FaCamera,
  FaSearch,
  FaBars,
  FaCog,
  FaHandshake,
  FaEnvelope,
  FaUserShield,
  FaFolder,
  FaBook,
  FaTshirt,
  FaLink,
  FaPoll,
} from 'react-icons/fa';
// Using simple up/down buttons instead of drag-drop for better compatibility
import {
  NavigationItem,
  SocialLink,
  getAllNavigationItems,
  createNavigationItem,
  updateNavigationItem,
  deleteNavigationItem,
  reorderNavigationItems,
  getAllSocialLinks,
  createSocialLink,
  updateSocialLink,
  deleteSocialLink,
  reorderSocialLinks,
  seedDefaultNavigation,
} from '../../services/navigation';
import { HelpTooltipCard } from '../../components/common/HelpTooltipCard';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useTranslation } from 'react-i18next';

const ADMIN_PAGE_PRESETS = [
  { value: 'dashboard', label: 'Nástěnka', url: '/admin' },
  { value: 'analytics', label: 'Analytika', url: '/admin/analytika' },
  { value: 'teams', label: 'Týmy', url: '/admin/tymy' },
  { value: 'matches', label: 'Zápasy', url: '/admin/zapasy' },
  { value: 'activities', label: 'Aktivity', url: '/admin/aktivity' },
  { value: 'players', label: 'Hráči', url: '/admin/hraci' },
  { value: 'articles', label: 'Články', url: '/admin/clanky' },
  { value: 'comments', label: 'Komentáře', url: '/admin/komentare' },
  { value: 'about', label: 'O klubu', url: '/admin/o-klubu' },
  { value: 'videos', label: 'Videa', url: '/admin/videa' },
  { value: 'gallery', label: 'Galerie', url: '/admin/galerie' },
  { value: 'banners', label: 'Bannery', url: '/admin/bannery' },
  { value: 'clothing', label: 'Oblečení', url: '/admin/obleceni' },
  { value: 'sponsors', label: 'Sponzoři', url: '/admin/sponzori' },
  { value: 'messages', label: 'Zprávy', url: '/admin/zpravy' },
  { value: 'contacts', label: 'Kontakty', url: '/admin/kontakty' },
  { value: 'newsletter', label: 'Zpravodaj', url: '/admin/newsletter' },
  { value: 'polls', label: 'Ankety', url: '/admin/ankety' },
  { value: 'sweepstakes', label: 'Soutěže', url: '/admin/sweepstakes' },
  { value: 'engagement', label: 'Odměny & Úspěchy', url: '/admin/engagement' },
  { value: 'navigation', label: 'Navigace', url: '/admin/navigace' },
  { value: 'competition_aliases', label: 'Alias soutěží', url: '/admin/aliasy-soutezi' },
  { value: 'users', label: 'Uživatelé', url: '/admin/uzivatele' },
  { value: 'settings', label: 'Nastavení', url: '/admin/nastaveni' },
  { value: 'files', label: 'Soubory', url: '/admin/soubory' },
  { value: 'scoreboard', label: 'Tabule (Scoreboard)', url: '/admin/scoreboard' },
  { value: 'scoreboard_remote', label: 'Scoreboard Remote', url: '/admin/scoreboard/remote' },
  { value: 'prefetch', label: 'Prefetch', url: '/admin/prefetch' },
  { value: 'docs', label: 'Dokumentace', url: '/admin/docs' },
  { value: 'webmail', label: 'Webmail', url: 'https://webmail.example.com' },
];

const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: FaFacebook },
  { value: 'instagram', label: 'Instagram', icon: FaInstagram },
  { value: 'youtube', label: 'YouTube', icon: FaYoutube },
  { value: 'twitter', label: 'Twitter', icon: FaTwitter },
  { value: 'tiktok', label: 'TikTok', icon: FaTiktok },
  { value: 'linkedin', label: 'LinkedIn', icon: FaLinkedin },
  { value: 'discord', label: 'Discord', icon: FaDiscord },
  { value: 'twitch', label: 'Twitch', icon: FaTwitch },
];

const NAV_ICON_OPTIONS = [
  { value: 'FaHome', label: 'Domů', icon: FaHome },
  { value: 'FaInfoCircle', label: 'O klubu', icon: FaInfoCircle },
  { value: 'FaCalendarAlt', label: 'Kalendář', icon: FaCalendarAlt },
  { value: 'FaFutbol', label: 'Hráči', icon: FaFutbol },
  { value: 'FaUsers', label: 'Týmy', icon: FaUsers },
  { value: 'FaTable', label: 'Tabulky', icon: FaTable },
  { value: 'FaNewspaper', label: 'Články', icon: FaNewspaper },
  { value: 'FaVideo', label: 'Videa', icon: FaVideo },
  { value: 'FaCamera', label: 'Galerie', icon: FaCamera },
  { value: 'FaHandshake', label: 'Sponzoři', icon: FaHandshake },
  { value: 'FaEnvelope', label: 'Kontakt', icon: FaEnvelope },
  { value: 'FaSearch', label: 'Hledat', icon: FaSearch },
  { value: 'FaBars', label: 'Menu', icon: FaBars },
  { value: 'FaLink', label: 'Odkaz', icon: FaLink },
  { value: 'FaCog', label: 'Nastavení', icon: FaCog },
  { value: 'FaPoll', label: 'Ankety', icon: FaPoll },
  { value: 'FaUserShield', label: 'Uživatelé', icon: FaUserShield },
  { value: 'FaFolder', label: 'Soubory', icon: FaFolder },
  { value: 'FaBook', label: 'Stránka', icon: FaBook },
  { value: 'FaTshirt', label: 'Oblečení', icon: FaTshirt },
];

const ICON_COMPONENTS: Record<string, any> = Object.fromEntries(NAV_ICON_OPTIONS.map(opt => [opt.value, opt.icon]));

// NavItemCard component for hierarchical display
interface NavItemCardProps {
  item: NavigationItem;
  index: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAddChild: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  cardBg: string;
  borderColor: string;
  hoverBg: string;
  level?: number;
  onChildMoveUp?: (parentId: number, index: number) => void;
  onChildMoveDown?: (parentId: number, index: number) => void;
  onToggleVisible: (item: NavigationItem) => void;
  childrenDroppableId?: string;
  draggableChildPrefix?: string;
  onEditTarget?: (item: NavigationItem) => void;
  onDeleteTarget?: (item: NavigationItem) => void;
}

const NavItemCard: React.FC<NavItemCardProps> = ({
  item,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  onAddChild,
  isExpanded,
  onToggleExpand,
  cardBg,
  borderColor,
  hoverBg,
  level = 0,
  onChildMoveUp,
  onChildMoveDown,
  onToggleVisible,
  childrenDroppableId,
  draggableChildPrefix,
  onEditTarget,
  onDeleteTarget,
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const indentPx = level * 32;
  const isCategory = item.type === 'dropdown';

  return (
    <Box ml={`${indentPx}px`}>
      <Card
        bg={item.visible ? cardBg : useColorModeValue('gray.100', 'gray.700')}
        borderWidth="1px"
        borderColor={borderColor}
        _hover={{ bg: hoverBg }}
        transition="all 0.2s"
      >
        <CardBody py={3}>
          <Flex align="center" gap={3}>
            {/* Expand/Collapse button for items with children */}
            {hasChildren ? (
              <IconButton
                aria-label="Toggle children"
                icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                size="sm"
                variant="ghost"
                onClick={onToggleExpand}
              />
            ) : (
              <Box w="32px" /> 
            )}

            {/* Reorder buttons */}
            <VStack spacing={0}>
              <IconButton
                aria-label="Nahoru"
                icon={<ChevronUpIcon />}
                size="xs"
                isDisabled={index === 0}
                onClick={onMoveUp}
                variant="ghost"
              />
              <IconButton
                aria-label="Dolů"
                icon={<ChevronDownIcon />}
                size="xs"
                isDisabled={index === total - 1}
                onClick={onMoveDown}
                variant="ghost"
              />
            </VStack>

            {/* Item info */}
            <VStack align="start" flex={1} spacing={1}>
              <HStack>
                <Text fontWeight="bold" fontSize="md">
                  {item.label}
                </Text>
                <Badge colorScheme={
                  item.type === 'external' ? 'orange' :
                  item.type === 'dropdown' ? 'purple' :
                  item.type === 'page' ? 'blue' : 'green'
                }>
                  {item.type}
                </Badge>
                {!item.visible && (
                  <Badge colorScheme="red">
                    <HStack spacing={1}>
                      <ViewOffIcon />
                      <Text>Skryto</Text>
                    </HStack>
                  </Badge>
                )}
              </HStack>
              <HStack spacing={2} fontSize="sm" color="gray.500">
                {item.url && (
                  <HStack>
                    <Text isTruncated maxW="300px">{item.url}</Text>
                    {item.type === 'external' && <ExternalLinkIcon />}
                  </HStack>
                )}
                {item.page_type && !item.url && (
                  <Text>Page: {item.page_type}</Text>
                )}
                {hasChildren && (
                  <Badge colorScheme="cyan">
                    {item.children!.length} {item.children!.length === 1 ? 'podpoložka' : 'podpoložek'}
                  </Badge>
                )}
              </HStack>
            </VStack>

            {/* Action buttons */}
            <HStack spacing={1}>
              <Tooltip label={item.visible ? 'Skrýt' : 'Zobrazit'}>
                <IconButton
                  aria-label={item.visible ? 'Skrýt' : 'Zobrazit'}
                  icon={item.visible ? <ViewOffIcon /> : <ViewIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onToggleVisible(item)}
                />
              </Tooltip>
              {item.type === 'dropdown' && (
                <Tooltip label="Přidat podpoložku">
                  <IconButton
                    aria-label="Přidat podpoložku"
                    icon={<AddIcon />}
                    size="sm"
                    colorScheme="green"
                    variant="ghost"
                    onClick={onAddChild}
                  />
                </Tooltip>
              )}
              <Tooltip label="Upravit">
                <IconButton
                  aria-label="Upravit"
                  icon={<EditIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => (typeof onEditTarget === 'function' ? onEditTarget(item) : onEdit && onEdit())}
                />
              </Tooltip>
              <Tooltip label="Smazat">
                <IconButton
                  aria-label="Smazat"
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => (typeof onDeleteTarget === 'function' ? onDeleteTarget(item) : onDelete && onDelete())}
                />
              </Tooltip>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Always render a children Droppable for categories (dropdown type).
          This allows dropping into collapsed or empty categories. */}
      {isCategory && (
        <Droppable droppableId={childrenDroppableId || `children-${item.id}`}> 
          {(provided) => (
            <VStack
              spacing={2}
              align="stretch"
              mt={2}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {hasChildren && isExpanded && item.children!.map((child, childIndex) => (
                <Draggable key={String(child.id)} draggableId={`${(draggableChildPrefix || 'child')}-${child.id}`} index={childIndex}>
                  {(dragProvided) => (
                    <Box ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                      <NavItemCard
                        key={child.id}
                        item={child}
                        index={childIndex}
                        total={item.children!.length}
                        onMoveUp={() => onChildMoveUp && onChildMoveUp(item.id!, childIndex)}
                        onMoveDown={() => onChildMoveDown && onChildMoveDown(item.id!, childIndex)}
                        onEdit={() => (typeof onEditTarget === 'function' ? onEditTarget(child) : onEdit && onEdit())}
                        onDelete={() => (typeof onDeleteTarget === 'function' ? onDeleteTarget(child) : onDelete && onDelete())}
                        onAddChild={() => {}}
                        isExpanded={false}
                        onToggleExpand={() => {}}
                        cardBg={cardBg}
                        borderColor={borderColor}
                        hoverBg={hoverBg}
                        level={level + 1}
                        onChildMoveUp={onChildMoveUp}
                        onChildMoveDown={onChildMoveDown}
                        onToggleVisible={onToggleVisible}
                        onEditTarget={onEditTarget}
                        onDeleteTarget={onDeleteTarget}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {/* Provide a minimal drop zone even when collapsed or empty */}
              {!hasChildren && (
                <Box minH="8px" />
              )}
              {provided.placeholder}
            </VStack>
          )}
        </Droppable>
      )}
    </Box>
  );
};

const NavigationAdminPage = () => {
  const { t } = useTranslation();
  
  const PAGE_TYPE_OPTIONS = [
    { value: 'home', label: 'Domů', url: '/' },
    { value: 'about', label: 'O klubu', url: '/o-klubu' },
    { value: 'calendar', label: 'Kalendář', url: '/kalendar' },
    { value: 'matches', label: 'Zápasy', url: '/zapasy' },
    { value: 'activities', label: 'Aktivity', url: '/aktivity' },
    { value: 'players', label: 'Hráči', url: '/hraci' },
    { value: 'tables', label: 'Tabulky', url: '/tabulky' },
    { value: 'blog', label: 'Články', url: '/blog' },
    { value: 'videos', label: 'Videa', url: '/videa' },
    { value: 'gallery', label: t('homepage.gallery'), url: '/galerie' },
    { value: 'sponsors', label: 'Sponzoři', url: '/sponzori' },
    { value: 'contact', label: 'Kontakt', url: '/kontakt' },
    { value: 'search', label: 'Hledat', url: '/hledat' },
  ];

  const [navItems, setNavItems] = useState<NavigationItem[]>([]);
  const [adminNavItems, setAdminNavItems] = useState<NavigationItem[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNav, setEditingNav] = useState<NavigationItem | null>(null);
  const [editingSocial, setEditingSocial] = useState<SocialLink | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [isAdminNav, setIsAdminNav] = useState(false);
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const { isOpen: isNavModalOpen, onOpen: onNavModalOpen, onClose: onNavModalClose } = useDisclosure();
  const { isOpen: isSocialModalOpen, onOpen: onSocialModalOpen, onClose: onSocialModalClose } = useDisclosure();
  const { confirm } = useConfirmDialog();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [navData, socialData] = await Promise.all([
        getAllNavigationItems(),
        getAllSocialLinks(),
      ]);

      // Auto-seed if navigation is empty
      if (!navData || navData.length === 0) {
        try {
          const seedResult = await seedDefaultNavigation();
          if (seedResult.seeded) {
            toast({
              title: 'Výchozí navigace vytvořena',
              description: `Automaticky vytvořeno ${seedResult.count} položek navigace`,
              status: 'success',
              duration: 4000,
              isClosable: true,
            });
            // Reload data after seeding
            const [newNavData, newSocialData] = await Promise.all([
              getAllNavigationItems(),
              getAllSocialLinks(),
            ]);
            const frontend = (newNavData || []).filter(item => !item.requires_admin);
            const admin = (newNavData || []).filter(item => item.requires_admin);
            setNavItems(frontend);
            setAdminNavItems(admin);
            setSocialLinks(newSocialData || []);
            return;
          }
        } catch (seedError) {
          console.error('Chyba při automatickém seedování:', seedError);
          // Continue with empty navigation
        }
      }
      
      // Split nav items into frontend and admin
      const frontend = (navData || []).filter(item => !item.requires_admin);
      const admin = (navData || []).filter(item => item.requires_admin);
      
      setNavItems(frontend);
      setAdminNavItems(admin);
      setSocialLinks(socialData || []);
    } catch (error: any) {
      console.error('Chyba při načítání navigace:', error);
      toast({
        title: 'Chyba při načítání dat',
        description: error?.response?.data?.error || error?.message || 'Neznámá chyba',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleVisible = async (target: NavigationItem) => {
    const newVisible = !target.visible;
    try {
      await updateNavigationItem(target.id!, { visible: newVisible } as any);
      const applyToggle = (list: NavigationItem[]): NavigationItem[] =>
        (list || []).map((it) => {
          if (it.id === target.id) return { ...it, visible: newVisible } as NavigationItem;
          const children = it.children ? applyToggle(it.children) : it.children;
          return { ...it, children } as NavigationItem;
        });
      setNavItems((prev) => applyToggle(prev));
      setAdminNavItems((prev) => applyToggle(prev));
      toast({ title: newVisible ? 'Zobrazeno' : 'Skryto', status: 'success', duration: 1500 });
    } catch (e) {
      toast({ title: 'Chyba při změně viditelnosti', status: 'error', duration: 3000 });
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const parseAdminChildrenId = (id: string) => id.startsWith('admin-children-') ? parseInt(id.replace('admin-children-', ''), 10) : null;
    const parseFrontChildrenId = (id: string) => id.startsWith('frontend-children-') ? parseInt(id.replace('frontend-children-', ''), 10) : null;

    if (source.droppableId === 'frontend-nav') {
      const items = Array.from(navItems);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      setNavItems(items);
      const orders = items.map((item, idx) => ({ id: item.id!, display_order: idx }));
      try {
        await reorderNavigationItems(orders);
        toast({ title: 'Pořadí aktualizováno', status: 'success', duration: 2000 });
      } catch (error) {
        toast({ title: 'Chyba při aktualizaci pořadí', status: 'error', duration: 3000 });
        loadData();
      }
    } else if (source.droppableId === 'admin-nav' && destination.droppableId === 'admin-nav') {
      const items = Array.from(adminNavItems);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      setAdminNavItems(items);
      const orders = items.map((item, idx) => ({ id: item.id!, display_order: idx }));
      try {
        await reorderNavigationItems(orders);
        toast({ title: 'Pořadí aktualizováno', status: 'success', duration: 2000 });
      } catch (error) {
        toast({ title: 'Chyba při aktualizaci pořadí', status: 'error', duration: 3000 });
        loadData();
      }
    } else if (
      source.droppableId.startsWith('admin-children-') || destination.droppableId.startsWith('admin-children-') ||
      (source.droppableId === 'admin-nav' && destination.droppableId.startsWith('admin-children-')) ||
      (source.droppableId.startsWith('admin-children-') && destination.droppableId === 'admin-nav')
    ) {
      const srcParentId = parseAdminChildrenId(source.droppableId);
      let destParentId = parseAdminChildrenId(destination.droppableId);
      const items = Array.from(adminNavItems);

      // Helper to find parent index by id
      const findParentIndex = (pid: number | null) => {
        if (pid === null) return -1;
        return items.findIndex((it) => it.id === pid);
      };

      // If dropping onto top-level container, but near a category card, treat as drop INTO that category
      let destChildIndex = destination.index;
      if (destParentId === null) {
        const at = items[destination.index];
        const before = destination.index > 0 ? items[destination.index - 1] : undefined;
        const after = destination.index < items.length - 1 ? items[destination.index + 1] : undefined;
        let dropIntoId: number | null = null;
        if (at && at.type === 'dropdown') dropIntoId = at.id!;
        else if (before && before.type === 'dropdown') dropIntoId = before.id!;
        else if (after && after.type === 'dropdown') dropIntoId = after.id!;
        if (dropIntoId) {
          destParentId = dropIntoId;
          const dIdxProbe = findParentIndex(destParentId);
          if (dIdxProbe >= 0) {
            destChildIndex = Array.isArray(items[dIdxProbe].children) ? items[dIdxProbe].children!.length : 0;
          } else {
            destChildIndex = 0;
          }
        }
      }

      // Fallback: if still no destination parent but we moved a sub-item, keep original parent to avoid promoting to top-level
      if (destParentId === null && srcParentId !== null) {
        destParentId = srcParentId;
        const dIdxProbe = findParentIndex(destParentId);
        destChildIndex = dIdxProbe >= 0 && Array.isArray(items[dIdxProbe].children) ? items[dIdxProbe].children!.length : 0;
      }

      let moved: NavigationItem | null = null;

      // Remove from source list
      if (srcParentId === null) {
        const [m] = items.splice(source.index, 1);
        moved = m;
      } else {
        const pIdx = findParentIndex(srcParentId);
        if (pIdx >= 0) {
          const srcChildren = Array.isArray(items[pIdx].children) ? Array.from(items[pIdx].children!) : [];
          const [m] = srcChildren.splice(source.index, 1);
          moved = m;
          items[pIdx] = { ...items[pIdx], children: srcChildren } as NavigationItem;
        }
      }

      if (!moved) return;

      // Insert into destination list
      if (destParentId === null) {
        items.splice(destination.index, 0, moved);
      } else {
        const dIdx = findParentIndex(destParentId);
        if (dIdx >= 0) {
          const destChildren = Array.isArray(items[dIdx].children) ? Array.from(items[dIdx].children!) : [];
          destChildren.splice(destChildIndex, 0, moved);
          items[dIdx] = { ...items[dIdx], children: destChildren } as NavigationItem;
        }
      }

      setAdminNavItems(items);

      // Persist parent change and reorder siblings at both source and destination
      try {
        await updateNavigationItem(moved.id!, { parent_id: destParentId === null ? null : destParentId, display_order: destParentId === null ? destination.index : destChildIndex } as any);

        // Reorder source siblings
        if (srcParentId === null) {
          const topOrders = items.map((it, idx) => ({ id: it.id!, display_order: idx }));
          await reorderNavigationItems(topOrders);
        } else {
          const srcIdx = findParentIndex(srcParentId);
          if (srcIdx >= 0) {
            const orders = (items[srcIdx].children || []).map((c, idx) => ({ id: c.id!, display_order: idx }));
            await reorderNavigationItems(orders);
          }
        }

        // Reorder destination siblings
        if (destParentId === null) {
          const topOrders = items.map((it, idx) => ({ id: it.id!, display_order: idx }));
          await reorderNavigationItems(topOrders);
        } else {
          const destIdx = findParentIndex(destParentId);
          if (destIdx >= 0) {
            const orders = (items[destIdx].children || []).map((c, idx) => ({ id: c.id!, display_order: idx }));
            await reorderNavigationItems(orders);
          }
        }

        toast({ title: 'Přesunuto', status: 'success', duration: 2000 });
      } catch (error) {
        toast({ title: 'Chyba při přesunu', status: 'error', duration: 3000 });
        loadData();
      }
    }
    // Frontend: moving between top-level and children or across categories
    else if (
      source.droppableId.startsWith('frontend-children-') || destination.droppableId.startsWith('frontend-children-') ||
      (source.droppableId === 'frontend-nav' && destination.droppableId.startsWith('frontend-children-')) ||
      (source.droppableId.startsWith('frontend-children-') && destination.droppableId === 'frontend-nav')
    ) {
      const srcParentId = parseFrontChildrenId(source.droppableId);
      let destParentId = parseFrontChildrenId(destination.droppableId);
      const items = Array.from(navItems);

      const findParentIndex = (pid: number | null) => {
        if (pid === null) return -1;
        return items.findIndex((it) => it.id === pid);
      };

      // If dropping onto top-level container, but near a category card, treat as drop INTO that category
      let destChildIndex = destination.index;
      if (destParentId === null) {
        const at = items[destination.index];
        const before = destination.index > 0 ? items[destination.index - 1] : undefined;
        let dropIntoId: number | null = null;
        if (at && at.type === 'dropdown') dropIntoId = at.id!;
        else if (before && before.type === 'dropdown') dropIntoId = before.id!;
        if (dropIntoId) {
          destParentId = dropIntoId;
          const dIdxProbe = findParentIndex(destParentId);
          if (dIdxProbe >= 0) {
            destChildIndex = Array.isArray(items[dIdxProbe].children) ? items[dIdxProbe].children!.length : 0;
          } else {
            destChildIndex = 0;
          }
        }
      }

      let moved: NavigationItem | null = null;

      if (srcParentId === null) {
        const [m] = items.splice(source.index, 1);
        moved = m;
      } else {
        const pIdx = findParentIndex(srcParentId);
        if (pIdx >= 0) {
          const srcChildren = Array.isArray(items[pIdx].children) ? Array.from(items[pIdx].children!) : [];
          const [m] = srcChildren.splice(source.index, 1);
          moved = m;
          items[pIdx] = { ...items[pIdx], children: srcChildren } as NavigationItem;
        }
      }

      if (!moved) return;

      if (destParentId === null) {
        items.splice(destination.index, 0, moved);
      } else {
        const dIdx = findParentIndex(destParentId);
        if (dIdx >= 0) {
          const destChildren = Array.isArray(items[dIdx].children) ? Array.from(items[dIdx].children!) : [];
          destChildren.splice(destChildIndex, 0, moved);
          items[dIdx] = { ...items[dIdx], children: destChildren } as NavigationItem;
        }
      }

      setNavItems(items);

      try {
        await updateNavigationItem(moved.id!, { parent_id: destParentId === null ? null : destParentId, display_order: destParentId === null ? destination.index : destChildIndex } as any);

        if (srcParentId === null) {
          const topOrders = items.map((it, idx) => ({ id: it.id!, display_order: idx }));
          await reorderNavigationItems(topOrders);
        } else {
          const srcIdx = findParentIndex(srcParentId);
          if (srcIdx >= 0) {
            const orders = (items[srcIdx].children || []).map((c, idx) => ({ id: c.id!, display_order: idx }));
            await reorderNavigationItems(orders);
          }
        }

        if (destParentId === null) {
          const topOrders = items.map((it, idx) => ({ id: it.id!, display_order: idx }));
          await reorderNavigationItems(topOrders);
        } else {
          const destIdx = findParentIndex(destParentId);
          if (destIdx >= 0) {
            const orders = (items[destIdx].children || []).map((c, idx) => ({ id: c.id!, display_order: idx }));
            await reorderNavigationItems(orders);
          }
        }

        toast({ title: 'Přesunuto', status: 'success', duration: 2000 });
      } catch (error) {
        toast({ title: 'Chyba při přesunu', status: 'error', duration: 3000 });
        loadData();
      }
    }
  };

  const moveChildNavItem = async (parentId: number, index: number, direction: 'up' | 'down') => {
    const moveWithin = async (
      list: NavigationItem[],
      setList: React.Dispatch<React.SetStateAction<NavigationItem[]>>
    ): Promise<boolean> => {
      const parentIdx = list.findIndex((it) => it.id === parentId);
      if (parentIdx === -1) return false;
      const parent = list[parentIdx];
      const children = Array.isArray(parent.children) ? [...parent.children] : [];
      if (children.length === 0) return true;
      if (direction === 'up' && index === 0) return true;
      if (direction === 'down' && index === children.length - 1) return true;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [children[index], children[targetIndex]] = [children[targetIndex], children[index]];

      const updatedParent: NavigationItem = { ...parent, children };
      const updated = [...list];
      updated[parentIdx] = updatedParent;
      setList(updated);

      const orders = children.map((c, idx) => ({ id: c.id!, display_order: idx }));
      try {
        await reorderNavigationItems(orders);
        toast({ title: 'Pořadí aktualizováno', status: 'success', duration: 2000 });
      } catch (err) {
        toast({ title: 'Chyba při aktualizaci pořadí', status: 'error', duration: 3000 });
        loadData();
      }
      return true;
    };

    const doneFront = await moveWithin(navItems, setNavItems);
    if (!doneFront) {
      await moveWithin(adminNavItems, setAdminNavItems);
    }
  };

  const moveNavItem = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === navItems.length - 1) return;

    const items = Array.from(navItems);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
    
    setNavItems(items);

    const orders = items.map((item, idx) => ({
      id: item.id!,
      display_order: idx,
    }));

    try {
      await reorderNavigationItems(orders);
      toast({
        title: 'Pořadí aktualizováno',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Chyba při aktualizaci pořadí',
        status: 'error',
        duration: 3000,
      });
      loadData();
    }
  };

  const moveAdminNavItem = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === adminNavItems.length - 1) return;

    const items = Array.from(adminNavItems);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
    
    setAdminNavItems(items);

    const orders = items.map((item, idx) => ({
      id: item.id!,
      display_order: idx,
    }));

    try {
      await reorderNavigationItems(orders);
      toast({
        title: 'Pořadí aktualizováno',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Chyba při aktualizaci pořadí',
        status: 'error',
        duration: 3000,
      });
      loadData();
    }
  };

  const moveSocialLink = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === socialLinks.length - 1) return;

    const items = Array.from(socialLinks);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
    
    setSocialLinks(items);

    const orders = items.map((item, idx) => ({
      id: item.id!,
      display_order: idx,
    }));

    try {
      await reorderSocialLinks(orders);
      toast({
        title: 'Pořadí aktualizováno',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Chyba při aktualizaci pořadí',
        status: 'error',
        duration: 3000,
      });
      loadData();
    }
  };

  const openNavModal = (item?: NavigationItem, parentId?: number, forAdmin?: boolean) => {
    setIsAdminNav(forAdmin || false);
    if (item) {
      setEditingNav(item);
    } else {
      const itemsCount = forAdmin ? adminNavItems.length : navItems.length;
      setEditingNav({
        label: '',
        type: forAdmin ? 'internal' : 'page',
        visible: true,
        display_order: itemsCount,
        target: '_self',
        parent_id: parentId,
        requires_admin: forAdmin || false,
        allow_editor: false,
      } as NavigationItem);
    }
    onNavModalOpen();
  };

  const toggleExpand = (id: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openSocialModal = (link?: SocialLink) => {
    if (link) {
      setEditingSocial(link);
    } else {
      setEditingSocial({
        platform: 'facebook',
        url: '',
        visible: true,
        display_order: socialLinks.length,
      } as SocialLink);
    }
    onSocialModalOpen();
  };

  const saveNavItem = async () => {
    if (!editingNav) return;

    try {
      if (editingNav.id) {
        await updateNavigationItem(editingNav.id, editingNav);
        toast({
          title: 'Položka aktualizována',
          status: 'success',
          duration: 2000,
        });
      } else {
        await createNavigationItem(editingNav);
        toast({
          title: 'Položka vytvořena',
          status: 'success',
          duration: 2000,
        });
      }
      onNavModalClose();
      loadData();
    } catch (error) {
      toast({
        title: 'Chyba při ukládání',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const saveSocialLink = async () => {
    if (!editingSocial) return;

    try {
      if (editingSocial.id) {
        await updateSocialLink(editingSocial.id, editingSocial);
        toast({
          title: 'Odkaz aktualizován',
          status: 'success',
          duration: 2000,
        });
      } else {
        await createSocialLink(editingSocial);
        toast({
          title: 'Odkaz vytvořen',
          status: 'success',
          duration: 2000,
        });
      }
      onSocialModalClose();
      loadData();
    } catch (error) {
      toast({
        title: 'Chyba při ukládání',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const deleteNav = async (id: number) => {
    const ok = await confirm({
      title: 'Smazat položku',
      message: 'Opravdu smazat tuto položku?',
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;

    try {
      await deleteNavigationItem(id);
      toast({
        title: 'Položka smazána',
        status: 'success',
        duration: 2000,
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Chyba při mazání',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const deleteSocial = async (id: number) => {
    const ok = await confirm({
      title: 'Smazat odkaz',
      message: 'Opravdu smazat tento odkaz?'
      ,
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;

    try {
      await deleteSocialLink(id);
      toast({
        title: 'Odkaz smazán',
        status: 'success',
        duration: 2000,
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Chyba při mazání',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const getSocialIcon = (platform: string) => {
    const found = SOCIAL_PLATFORMS.find(p => p.value === platform);
    return found?.icon || FaFacebook;
  };

  const handleSeedDefaultNavigation = async () => {
    const ok = await confirm({
      title: 'Vytvořit výchozí navigaci',
      message: 'Vytvořit výchozí navigační položky? Toto lze provést pouze pokud databáze je prázdná.',
      confirmText: 'Vytvořit',
      cancelText: 'Zrušit',
      isDanger: false,
    });
    if (!ok) return;

    try {
      const result = await seedDefaultNavigation();
      
      if (result.seeded) {
        toast({
          title: 'Výchozí navigace vytvořena',
          description: `Vytvořeno ${result.count} položek`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadData();
      } else {
        toast({
          title: 'Navigace již existuje',
          description: result.message,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Chyba při seedování navigace:', error);
      toast({
        title: 'Chyba při vytváření výchozí navigace',
        description: error?.response?.data?.error || error?.message || 'Neznámá chyba',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Container maxW="7xl" py={8}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Načítání...</Text>
          </VStack>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container maxW="7xl" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading size="lg">Správa navigace</Heading>
          <Button onClick={loadData} size="sm" colorScheme="gray">
            Obnovit data
          </Button>
        </HStack>

        <Alert status="info" variant="left-accent">
          <AlertIcon />
          <Box flex="1">
            <HStack spacing={4}>
              <Text fontSize="sm">
                <strong>Načteno:</strong> {navItems.length} webových, {adminNavItems.length} admin
              </Text>
            </HStack>
          </Box>
        </Alert>

        <Alert status="info">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Oddělená správa navigace</Text>
            <Text fontSize="sm" mt={1}>
              • <strong>Webová navigace:</strong> Menu na veřejném webu<br/>
              • <strong>Admin panel:</strong> Postranní menu v administraci
            </Text>
          </Box>
        </Alert>

        <DragDropContext onDragEnd={onDragEnd}>
        <Tabs>
          <TabList>
            <Tab>Webová navigace</Tab>
            <Tab>Admin panel</Tab>
          </TabList>

          <TabPanels>
            {/* Frontend Navigation Items Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={() => openNavModal()}>
                    Přidat hlavní položku
                  </Button>
                  <Text fontSize="sm" color="gray.500">
                    Přetahujte položky nahoru/dolů pro změnu pořadí. Klikněte na šipku pro zobrazení podpoložek.
                  </Text>
                </HStack>

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Tipy pro správu navigace:</Text>
                    <Text fontSize="sm" mt={1}>
                      • Použijte typ "Dropdown" pro položky s podpoložkami<br/>
                      • Podpoložky se zobrazí při najetí myší na hlavní položku<br/>
                      • Položky můžete skrýt bez smazání pomocí přepínače viditelnosti
                    </Text>
                  </Box>
                </Alert>

                <VStack spacing={2} align="stretch">
                  {navItems.length === 0 ? (
                    <Alert status="warning" variant="left-accent">
                      <AlertIcon />
                      <Box flex="1">
                        <Text fontWeight="bold">Žádné položky navigace</Text>
                        <Text fontSize="sm" mt={1} mb={2}>
                          Nebyly nalezeny žádné položky navigace. Můžete vytvořit výchozí navigaci nebo přidat položky ručně.
                        </Text>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleSeedDefaultNavigation}
                          >
                            Vytvořit výchozí navigaci
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openNavModal()}
                          >
                            Přidat položku ručně
                          </Button>
                        </HStack>
                      </Box>
                    </Alert>
                  ) : (
                    <Droppable droppableId="frontend-nav">
                      {(provided) => (
                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                          {navItems.map((item, index) => (
                            <Draggable key={String(item.id)} draggableId={`nav-${item.id}`} index={index}>
                              {(dragProvided) => (
                                <Box ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                                  <NavItemCard
                                    item={item}
                                    index={index}
                                    total={navItems.length}
                                    onMoveUp={() => moveNavItem(index, 'up')}
                                    onMoveDown={() => moveNavItem(index, 'down')}
                                    onEdit={() => openNavModal(item)}
                                    onDelete={() => deleteNav(item.id!)}
                                    onAddChild={() => openNavModal(undefined, item.id)}
                                    isExpanded={expandedItems.has(item.id!)}
                                    onToggleExpand={() => toggleExpand(item.id!)}
                                    cardBg={cardBg}
                                    borderColor={borderColor}
                                    hoverBg={hoverBg}
                                    onChildMoveUp={(parentId, childIdx) => moveChildNavItem(parentId, childIdx, 'up')}
                                    onChildMoveDown={(parentId, childIdx) => moveChildNavItem(parentId, childIdx, 'down')}
                                    onToggleVisible={toggleVisible}
                                    childrenDroppableId={`frontend-children-${item.id}`}
                                    draggableChildPrefix={'front-child'}
                                    onEditTarget={(it) => openNavModal(it)}
                                    onDeleteTarget={(it) => deleteNav(it.id!)}
                                  />
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  )}
                </VStack>
              </VStack>
            </TabPanel>

            {/* Admin Panel Navigation Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Button leftIcon={<AddIcon />} colorScheme="purple" onClick={() => openNavModal(undefined, undefined, true)}>
                    Přidat položku do admin panelu
                  </Button>
                  <Text fontSize="sm" color="gray.500">
                    Správa bočního menu v administraci
                  </Text>
                </HStack>

                <Alert status="info" borderRadius="md" colorScheme="purple">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Správa admin panelu:</Text>
                    <Text fontSize="sm" mt={1}>
                      • Vyberte z přednastavených stránek nebo přidejte vlastní<br/>
                      • Skryjte nepotřebné sekce pomocí viditelnosti<br/>
                      • Přidejte externí odkazy (např. Webmail)<br/>
                      • Kategorizujte pomocí dropdown menu
                    </Text>
                  </Box>
                </Alert>

                <VStack spacing={2} align="stretch">
                  <Droppable droppableId="admin-nav">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {adminNavItems.map((item, index) => (
                          <Draggable key={String(item.id)} draggableId={`admin-${item.id}`} index={index}>
                            {(dragProvided) => (
                              <Box ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                                <NavItemCard
                                  item={item}
                                  index={index}
                                  total={adminNavItems.length}
                                  onMoveUp={() => moveAdminNavItem(index, 'up')}
                                  onMoveDown={() => moveAdminNavItem(index, 'down')}
                                  onEdit={() => openNavModal(item, undefined, true)}
                                  onDelete={() => deleteNav(item.id!)}
                                  onAddChild={() => openNavModal(undefined, item.id, true)}
                                  isExpanded={expandedItems.has(item.id!)}
                                  onToggleExpand={() => toggleExpand(item.id!)}
                                  cardBg={cardBg}
                                  borderColor={borderColor}
                                  hoverBg={hoverBg}
                                  onChildMoveUp={(parentId, childIdx) => moveChildNavItem(parentId, childIdx, 'up')}
                                  onChildMoveDown={(parentId, childIdx) => moveChildNavItem(parentId, childIdx, 'down')}
                                  onToggleVisible={toggleVisible}
                                  childrenDroppableId={`admin-children-${item.id}`}
                                  draggableChildPrefix={'admin-child'}
                                  onEditTarget={(it) => openNavModal(it, undefined, true)}
                                  onDeleteTarget={(it) => deleteNav(it.id!)}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                  {adminNavItems.length === 0 && (
                    <Alert status="warning">
                      <AlertIcon />
                      Žádné vlastní položky v admin panelu. Použijte tlačítko "Přidat položku" pro vytvoření vlastní navigace.
                    </Alert>
                  )}
                </VStack>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
      </DragDropContext>
      </VStack>
      </Container>

      <Modal isOpen={isNavModalOpen} onClose={onNavModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingNav?.id ? 'Upravit položku' : 'Nová položka'}
            {isAdminNav && <Badge ml={2} colorScheme="purple">Admin panel</Badge>}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {isAdminNav && !editingNav?.id && (
                <Alert status="info" fontSize="sm">
                  <AlertIcon />
                  Vytvářejte položku pro boční menu v administraci. Můžete vybrat přednastavené stránky nebo přidat vlastní odkazy.
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel>Název</FormLabel>
                <Input
                  value={editingNav?.label || ''}
                  onChange={(e) => setEditingNav({ ...editingNav!, label: e.target.value })}
                  placeholder={isAdminNav ? 'Např. Nástěnka, Webmail' : 'Např. Domů, O klubu'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ '.chakra-form__required-indicator': { display: 'none' } }}
                >
                  <HStack spacing={1}>
                    <Box>Typ</Box>
                    <Text as="span" color="red.500">*</Text>
                  </HStack>
                  <HelpTooltipCard
                    label="Jaký typ zvolit"
                    title="Typ navigační položky"
                    items={[
                      'Stránka / Admin stránka – odkaz na existující stránku v rámci webu nebo administrace.',
                      'Interní odkaz – vlastní URL v rámci webu (např. /info/partners).',
                      'Externí odkaz – odkaz mimo web (např. klubový webmail, svazový web).',
                      'Dropdown / Kategorie – skupina, která sama neotevírá stránku, ale obsahuje podpoložky.',
                    ]}
                  />
                </FormLabel>
                <Select
                  value={editingNav?.type || (isAdminNav ? 'internal' : 'page')}
                  onChange={(e) => setEditingNav({ ...editingNav!, type: e.target.value as any })}
                >
                  {isAdminNav ? (
                    <>
                      <option value="internal">Admin stránka (vyberte existující)</option>
                      <option value="external">Externí odkaz (např. Webmail)</option>
                      <option value="dropdown">Kategorie (má podpoložky)</option>
                    </>
                  ) : (
                    <>
                      <option value="page">Stránka (vyberte existující)</option>
                      <option value="internal">Interní odkaz (vlastní URL)</option>
                      <option value="external">Externí odkaz</option>
                      <option value="dropdown">Dropdown (má podpoložky)</option>
                    </>
                  )}
                </Select>
              </FormControl>

              {editingNav?.type === 'page' && !isAdminNav && (
                <FormControl isRequired>
                  <FormLabel>Webová stránka</FormLabel>
                  <Select
                    value={editingNav?.page_type || ''}
                    onChange={(e) => {
                      const selected = PAGE_TYPE_OPTIONS.find(opt => opt.value === e.target.value);
                      setEditingNav({
                        ...editingNav!,
                        page_type: e.target.value,
                        url: selected?.url || '',
                        label: editingNav?.label || selected?.label || ''
                      });
                    }}
                  >
                    <option value="">-- Vyberte stránku --</option>
                    {PAGE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.url})
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {editingNav?.type === 'internal' && isAdminNav && (
                <FormControl isRequired>
                  <FormLabel>Admin stránka</FormLabel>
                  <Select
                    value={editingNav?.page_type || ''}
                    onChange={(e) => {
                      const selected = ADMIN_PAGE_PRESETS.find(opt => opt.value === e.target.value);
                      const isExternal = selected?.url?.startsWith('http');
                      setEditingNav({
                        ...editingNav!,
                        page_type: e.target.value,
                        url: selected?.url || '',
                        label: editingNav?.label || selected?.label || '',
                        type: isExternal ? 'external' : 'internal',
                      });
                    }}
                  >
                    <option value="">-- Vyberte admin stránku --</option>
                    <optgroup label="Hlavní">
                      <option value="dashboard">Nástěnka (/admin)</option>
                      <option value="analytics">Analytika (/admin/analytika)</option>
                    </optgroup>
                    <optgroup label="Obsah">
                      <option value="teams">Týmy (/admin/tymy)</option>
                      <option value="matches">Zápasy (/admin/zapasy)</option>
                      <option value="activities">Aktivity (/admin/aktivity)</option>
                      <option value="players">Hráči (/admin/hraci)</option>
                      <option value="articles">Články (/admin/clanky)</option>
                      <option value="categories">Kategorie (/admin/kategorie)</option>
                      <option value="comments">Komentáře (/admin/komentare)</option>
                      <option value="videos">Videa (/admin/videa)</option>
                      <option value="gallery">Galerie (/admin/galerie)</option>
                    </optgroup>
                    <optgroup label="Marketing">
                      <option value="sponsors">Sponzoři (/admin/sponzori)</option>
                      <option value="banners">Bannery (/admin/bannery)</option>
                      <option value="shortlinks">Zkrácené odkazy (/admin/shortlinks)</option>
                      <option value="polls">Ankety (/admin/ankety)</option>
                      <option value="sweepstakes">Soutěže (/admin/sweepstakes)</option>
                      <option value="engagement">Odměny & Úspěchy (/admin/engagement)</option>
                    </optgroup>
                    <optgroup label="Nástroje">
                      <option value="scoreboard">Tabule (Scoreboard) (/admin/scoreboard)</option>
                      <option value="scoreboard_remote">Scoreboard Remote (/admin/scoreboard/remote)</option>
                      <option value="clothing">Oblečení (/admin/obleceni)</option>
                    </optgroup>
                    <optgroup label="Komunikace">
                      <option value="messages">Zprávy (/admin/zpravy)</option>
                      <option value="contacts">Kontakty (/admin/kontakty)</option>
                      <option value="newsletter">Zpravodaj (/admin/newsletter)</option>
                    </optgroup>
                    <optgroup label="Nastavení">
                      <option value="navigation">Navigace (/admin/navigace)</option>
                      <option value="competition_aliases">Alias soutěží (/admin/aliasy-soutezi)</option>
                      <option value="users">Uživatelé (/admin/uzivatele)</option>
                      <option value="settings">Nastavení (/admin/nastaveni)</option>
                      <option value="files">Soubory (/admin/soubory)</option>
                      <option value="prefetch">Prefetch (/admin/prefetch)</option>
                      <option value="docs">Dokumentace (/admin/docs)</option>
                    </optgroup>
                    <optgroup label="Externí odkazy">
                      <option value="webmail">Webmail (vlastní URL)</option>
                    </optgroup>
                  </Select>
                </FormControl>
              )}

              {editingNav?.type === 'dropdown' && (
                <Alert status="info" fontSize="sm">
                  <AlertIcon />
                  Po vytvoření této položky můžete přidat podpoložky kliknutím na tlačítko "Přidat podpoložku".
                </Alert>
              )}

              {(editingNav?.type === 'internal' || editingNav?.type === 'external') && (
                <FormControl isRequired>
                  <FormLabel>URL</FormLabel>
                  <Input
                    value={editingNav?.url || ''}
                    onChange={(e) => setEditingNav({ ...editingNav!, url: e.target.value })}
                    placeholder={editingNav?.type === 'external' ? 'https://example.com' : '/vlastni-stranka'}
                  />
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Ikona</FormLabel>
                <Select
                  value={editingNav?.icon || ''}
                  onChange={(e) => setEditingNav({ ...editingNav!, icon: e.target.value || undefined })}
                >
                  <option value="">Bez ikony</option>
                  {NAV_ICON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
                {editingNav?.icon && (
                  <HStack mt={2} spacing={2} align="center">
                    <Icon as={ICON_COMPONENTS[editingNav.icon]} boxSize={5} />
                    <Text fontSize="sm">{editingNav.icon}</Text>
                  </HStack>
                )}
              </FormControl>

              {editingNav?.parent_id && (
                <Alert status="warning" fontSize="sm">
                  <AlertIcon />
                  Toto je podpoložka. Zobrazí se v dropdown menu rodičovské položky.
                </Alert>
              )}

              <FormControl>
                <FormLabel>Popis (volitelné)</FormLabel>
                <Textarea
                  value={editingNav?.css_class || ''}
                  onChange={(e) => setEditingNav({ ...editingNav!, css_class: e.target.value })}
                  placeholder="Krátký popis pro administrátory"
                  rows={2}
                />
              </FormControl>

              {editingNav?.type === 'external' && (
                <FormControl>
                  <FormLabel>Target</FormLabel>
                  <Select
                    value={editingNav?.target || '_self'}
                    onChange={(e) => setEditingNav({ ...editingNav!, target: e.target.value as any })}
                  >
                    <option value="_self">Stejné okno</option>
                    <option value="_blank">Nové okno</option>
                  </Select>
                </FormControl>
              )}

              {isAdminNav && editingNav?.type !== 'dropdown' && (
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Povolit editorům</FormLabel>
                  <Switch
                    isChecked={!!editingNav?.allow_editor}
                    onChange={(e) => setEditingNav({ ...editingNav!, allow_editor: e.target.checked })}
                  />
                </FormControl>
              )}

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Viditelné</FormLabel>
                <Switch
                  isChecked={editingNav?.visible ?? true}
                  onChange={(e) => setEditingNav({ ...editingNav!, visible: e.target.checked })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNavModalClose}>Zrušit</Button>
            <Button colorScheme="blue" onClick={saveNavItem}>Uložit</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </AdminLayout>
  );
};

export default NavigationAdminPage;
